import { paths } from '@/constants/paths';
import { CrystallizeRootPaths } from '@/ui/utils/localization-helpers/types/RequestContext';
import { extractAndRemoveHash } from '@/utils/url-helper';
import { fallbackCountry, fallbackLanguage } from '@/integrations/i18n/settings';
import { Localization } from './types';
import { Area, Country, LanguageCountryList } from './types/localization';

/**
 * Defines country groups available for checkout based on main shop localization.
 * If the main shop localization matches a group, all countries in that group are available.
 * Example: 'us' enables only 'us', later also 'ca' and 'mx' when expanded.
 * If not in any group, all countries are available except those explicitly listed.
 */
export const checkoutCountryLimitations = [
  ['us'], // Hermes countries: currently only us, later ca, mx
  // Future groups can be added here if needed
];

/**
 * Filters the checkout available countries based on the main country localization.
 *
 * @param countries - Array of all countries to be filtered.
 * @param mainCountry - The ISO code of the main localization country.
 * @returns Array of countries that are available for checkout based on main localization country.
 */
export function filterCheckoutAvailableCountries(
  countries: Country[],
  mainCountry: string,
): Country[] {
  // If catalogue separation is not enabled by env var, return all countries
  if (process.env.ENABLE_HERMES_SEPARATION !== 'true') {
    return countries;
  }

  const group = checkoutCountryLimitations.find((g) => g.includes(mainCountry));
  if (group) {
    return countries.filter((c) => group.includes(c.code));
  }

  // If main country is not in any group, filter out the countries from checkoutCountryLimitations
  const limitedCountries = new Set(checkoutCountryLimitations.flat());
  return countries.filter((c) => !limitedCountries.has(c.code));
}

/**
 * Function to check if a language is available in Crystallize.
 * @param languageCountryList - The list of available languages and countries.
 * @param language - The language code to check.
 * @returns True if the language is available, otherwise false.
 */
export function isValidLanguage(
  languageCountryList: LanguageCountryList,
  language?: string,
): boolean {
  if (!language) {
    return false;
  }
  const languageExists = languageCountryList.languages.find(
    (languageCountry) => languageCountry.code.toLowerCase() === language.toLowerCase(),
  );
  return !!languageExists;
}

/**
 * Function to check if a country is available in Crystallize.
 * @param languageCountryList - The list of available languages and countries.
 * @param country - The country code to check.
 * @returns True if the country is available, otherwise false.
 */
export function isValidCountry(
  languageCountryList: LanguageCountryList,
  country?: string,
): boolean {
  if (!country) {
    return false;
  }
  const countryExists = languageCountryList.countries.find(
    (languageCountry) => languageCountry.code.toLowerCase() === country.toLowerCase(),
  );
  return !!countryExists;
}

/**
 * Function to check if both language and country are available in Crystallize.
 * @param languageCountryList - The list of available languages and countries.
 * @param language - The language code to check.
 * @param country - The country code to check.
 * @returns True if both the language and country are available, otherwise false.
 */
export function isValidLanguageCountry(
  languageCountryList: LanguageCountryList,
  language?: string,
  country?: string,
): boolean {
  if (!language || !country) {
    return false;
  }
  const languageExists = isValidLanguage(languageCountryList, language);
  const countryExists = isValidCountry(languageCountryList, country);
  return languageExists && countryExists;
}

/**
 * Function to check if a given country has multiple areas.
 * @param languageCountryList - The list of available languages and countries.
 * @param country - The country code to check.
 * @returns The areas if the country has multiple areas, otherwise null.
 */
export function hasCountryMoreAreas(
  languageCountryList: LanguageCountryList | Country[],
  country?: string,
): Area[] | null {
  if (!country) {
    return null;
  }
  let countries: Country[] = [];
  if ('countries' in languageCountryList) {
    countries = languageCountryList.countries;
  } else {
    countries = languageCountryList;
  }

  const countryData = countries.find((cnt) => cnt.code.toLowerCase() === country.toLowerCase());
  const areas = countryData?.areas;

  if (areas && areas.length > 1) {
    return areas;
  }
  return null;
}

/**
 * Function to check if language and country are valid and available in Crystallize.
 * @param languageCountryList - The list of available languages and countries.
 * @param localization - The localization object containing language and country.
 * @returns True if the language and country are valid and available, otherwise false.
 */
export function isValidLocalization(
  languageCountryList: LanguageCountryList,
  localization: Localization | null,
): boolean {
  if (!localization?.language || !localization?.country) {
    return false;
  }
  const languageExists = isValidLanguage(languageCountryList, localization.language);
  const countryExists = isValidCountry(languageCountryList, localization.country);

  return languageExists && countryExists;
}

/**
 * Function to get the Crystallize root folder based on the country.
 * @param country - The country code to check.
 * @returns The root folder path as a string.
 */
export function getCrystallizeRootFolders(country: string): CrystallizeRootPaths {
  if (process.env.ENABLE_HERMES_SEPARATION !== 'true') {
    return { root: paths.crystallizeRoot, global: paths.crystallizeGlobal };
  }

  if (country.toLowerCase() === 'us') {
    return { root: paths.crystallizeRootHermes, global: paths.crystallizeGlobalHermes };
  }

  return { root: paths.crystallizeRoot, global: paths.crystallizeGlobal };
}

/**
 * Returns the URL-facing locale prefix.
 * International locales (country === fallbackCountry) → just language: 'en'
 * Country-specific → full format: 'de-de'
 * Undefined inputs fall back to fallbackLanguage / fallbackCountry so callers
 * cannot silently produce 'undefined-undefined' URLs.
 */
export function getLocalePrefix(
  language: string | undefined,
  country: string | undefined,
): string {
  const resolvedLanguage = language ?? fallbackLanguage;
  const resolvedCountry = country ?? fallbackCountry;
  return resolvedCountry === fallbackCountry
    ? resolvedLanguage
    : `${resolvedLanguage}-${resolvedCountry}`;
}

/**
 * Normalizes a CMS seoLang.key from 'xx-<fallbackCountry>' format to 'xx'.
 * Leaves country-specific keys like 'de-de' unchanged.
 */
export function normalizeSeoLangKey(key: string): string {
  const [lang, country] = key.split('-');
  return getLocalePrefix(lang, country ?? fallbackCountry);
}

export function constructURL(urlObject: URL, localization: Localization, path: string): string {
  // Strip any legacy area parameter so localized URLs stay clean (area is no longer URL-encoded).
  urlObject.searchParams.delete('area');

  // Extract hash fragment from the path, if any
  const { pathWithoutHash, hashFragment } = extractAndRemoveHash(path);

  // Normalize the path: remove leading/trailing slashes
  const normalizedPath = pathWithoutHash.replaceAll(/(^\/)|(\/$)/g, '');

  // Convert existing search params to string
  const existingParamsString = urlObject.searchParams.toString();
  const searchPart = existingParamsString ? `?${existingParamsString}` : '';

  // Construct the base URL
  let url = `${urlObject.origin}/${getLocalePrefix(localization.language, localization.country)}`;
  if (normalizedPath) {
    url += `/${normalizedPath}`;
  }

  // Append search and hash (in the correct order)
  url += searchPart;
  if (hashFragment) {
    url += `#${hashFragment}`;
  }

  return url;
}
